import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import _ from 'lodash';
import { Modal, Tab } from 'semantic-ui-react';
import { DyzioModalHeader } from 'dyzio-shared-components/lib/components/dyzio-modal';

import { PAGE_SIZE, PLATFORMS } from './constants';
import fetchStories from './stories-api';
import StoryFilters from './StoryFilters';
import StoriesGrid from './StoriesGrid';

const styles = {
  layout: { display: 'flex', alignItems: 'flex-start' },
  filterColumn: { flex: '0 0 200px', paddingRight: '1.5em', alignSelf: 'flex-start' },
  // The right column is its own scroll area, so the tab menu pins to the top of
  // it — no Modal.Content padding above the menu for cards to peek through.
  storiesColumn: {
    flex: '1 1 0', minWidth: 0, maxHeight: '70vh', overflowY: 'auto', paddingRight: '4px',
  },
  stickyMenu: {
    position: 'sticky', top: 0, zIndex: 2, background: '#fff',
  },
};

const TAB_MENU = { attached: true, tabular: true, style: styles.stickyMenu };

const emptyPlatform = () => ({
  stories: [], cursor: null, loading: false, error: null, loaded: false,
});

class AllStoriesModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      activeIndex: 0,
      startDate: null,
      endDate: null,
      matchedOnly: false,
      sortNewest: true,
      instagram: emptyPlatform(),
      tiktok: emptyPlatform(),
      // assetId → true; seeded with stories already on the collaboration.
      added: _.keyBy(props.existingAssetIds, _.identity),
      adding: {},
    };
  }

  componentDidMount() {
    this.loadPage(this.activePlatform());
  }

  activePlatform = () => PLATFORMS[this.state.activeIndex].key;

  buildParams = cursor => {
    const {
      startDate, endDate, matchedOnly, sortNewest,
    } = this.state;
    const params = { limit: PAGE_SIZE, sort: sortNewest ? 'desc' : 'asc' };
    if (cursor) params.cursor = cursor;
    if (startDate) params.start = moment(startDate).format('YYYY-MM-DD');
    if (endDate) params.end = moment(endDate).format('YYYY-MM-DD');
    if (matchedOnly) params.matched = true;
    return params;
  }

  // append=false starts a fresh list (first page / after a filter change);
  // append=true pages on via the stored cursor.
  loadPage = (platform, { append = false } = {}) => {
    const { discussionId } = this.props;
    const current = this.state[platform];
    if (current.loading) return;
    const cursor = append ? current.cursor : null;

    this.setState({ [platform]: { ...current, loading: true, error: null, loaded: true } });

    fetchStories(platform, discussionId, this.buildParams(cursor))
      .then(({ stories, nextCursor }) => this.setState(prev => ({
        [platform]: {
          ...prev[platform],
          stories: append ? [...prev[platform].stories, ...stories] : stories,
          cursor: nextCursor,
          loading: false,
        },
      })))
      .catch(err => this.setState(prev => ({
        [platform]: {
          ...prev[platform],
          loading: false,
          error: _.get(err, 'response.data.error', err.message),
        },
      })));
  }

  setStoriesRef = el => { this.storiesEl = el; }

  handleTabChange = (e, { activeIndex }) => {
    this.setState({ activeIndex }, () => {
      if (this.storiesEl) this.storiesEl.scrollTop = 0;
      const platform = this.activePlatform();
      if (!this.state[platform].loaded) this.loadPage(platform);
    });
  }

  // Filters + sort apply server-side, so reset both platforms and refetch the
  // active one from page 1; the other reloads lazily when its tab opens.
  reloadWithFilters = () => {
    if (this.storiesEl) this.storiesEl.scrollTop = 0;
    this.setState(
      { instagram: emptyPlatform(), tiktok: emptyPlatform() },
      () => this.loadPage(this.activePlatform()),
    );
  }

  setStartDate = date => this.setState({ startDate: date }, this.reloadWithFilters)

  setEndDate = date => this.setState({ endDate: date }, this.reloadWithFilters)

  toggleMatched = (e, { checked }) => this.setState({ matchedOnly: checked }, this.reloadWithFilters)

  changeSort = (e, { value }) => this.setState({ sortNewest: value === 'newest' }, this.reloadWithFilters)

  // Delegates the create to the parent (onAddStory) and tracks per-card state by assetId.
  addStory = story => {
    const { onAddStory } = this.props;
    const key = story.assetId;
    if (!key || this.state.adding[key] || this.state.added[key]) return;

    this.setState(prev => ({ adding: { ...prev.adding, [key]: true } }));
    Promise.resolve(onAddStory(story))
      .then(() => this.setState(prev => ({
        adding: _.omit(prev.adding, key),
        added: { ...prev.added, [key]: true },
      })))
      .catch(() => this.setState(prev => ({ adding: _.omit(prev.adding, key) })));
  }

  render() {
    const { onClose } = this.props;
    const {
      activeIndex, startDate, endDate, matchedOnly, sortNewest, added, adding,
    } = this.state;

    const panes = PLATFORMS.map(platform => ({
      menuItem: { key: platform.key, icon: platform.icon, content: platform.label },
      render: () => (
        <Tab.Pane>
          <StoriesGrid
            stories={this.state[platform.key].stories}
            loading={this.state[platform.key].loading}
            error={this.state[platform.key].error}
            cursor={this.state[platform.key].cursor}
            onLoadMore={() => this.loadPage(platform.key, { append: true })}
            added={added}
            adding={adding}
            onAddStory={this.addStory}
          />
        </Tab.Pane>
      ),
    }));

    return (
      <Modal open onClose={onClose} size="large">
        <DyzioModalHeader headerText="All stories" onClose={onClose} />
        <Modal.Content>
          <div style={styles.layout}>
            <div style={styles.filterColumn}>
              <StoryFilters
                startDate={startDate}
                endDate={endDate}
                matchedOnly={matchedOnly}
                sortNewest={sortNewest}
                onStartChange={this.setStartDate}
                onEndChange={this.setEndDate}
                onToggleMatched={this.toggleMatched}
                onSortChange={this.changeSort}
                onClear={this.clearFilters}
              />
            </div>
            <div style={styles.storiesColumn} ref={this.setStoriesRef}>
              <Tab
                activeIndex={activeIndex}
                onTabChange={this.handleTabChange}
                menu={TAB_MENU}
                panes={panes}
              />
            </div>
          </div>
        </Modal.Content>
      </Modal>
    );
  }
}

AllStoriesModal.propTypes = {
  discussionId: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onAddStory: PropTypes.func.isRequired,
  existingAssetIds: PropTypes.arrayOf(PropTypes.string),
};

AllStoriesModal.defaultProps = {
  existingAssetIds: [],
};

export default AllStoriesModal;



/** WEBPACK FOOTER **
 ** ./src/components/all-stories-modal/index.js
 **/